import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, UploadSimple, MagnifyingGlass, ArrowUp, ArrowDown, X } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { STAGES, groupSessionsByBlock, formatDateShortUK } from "./_helpers";
import { BulkMoveButton, MoveRoomButton } from "./MoveRoomButtons";

export default function SessionsAdmin() {
  const [rows, setRows] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", date: "2026-06-22", start_time: "09:00", end_time: "10:30", stage: "Plenary", track: "", chair: "", description: "", speaker_ids: [], resources: "", replay_url: "" };
  const [form, setForm] = useState(empty);
  const [speakerSearch, setSpeakerSearch] = useState("");
  // Citations are managed as a structured list in the UI; on save we serialize
  // back to the `resources` field as one citation per blank line (the same
  // wire format the backend, drip-release, and delegate-side parsing expect).
  const [citations, setCitations] = useState([""]);

  const parseCitations = (text) => {
    const parts = (text || "").split(/\n{2,}/).map((c) => c.trim()).filter(Boolean);
    return parts.length ? parts : [""];
  };
  const updateCitation = (i, val) => setCitations((cs) => cs.map((c, j) => (j === i ? val : c)));
  const addCitation = () => setCitations((cs) => [...cs, ""]);
  const removeCitation = (i) => setCitations((cs) => {
    const next = cs.filter((_, j) => j !== i);
    return next.length ? next : [""];
  });
  const moveCitation = (i, dir) => setCitations((cs) => {
    const j = i + dir;
    if (j < 0 || j >= cs.length) return cs;
    const next = [...cs];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const load = async () => {
    const [s, sp] = await Promise.all([api.get("/sessions"), api.get("/speakers")]);
    setRows(s.data); setSpeakers(sp.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setSpeakerSearch(""); setCitations([""]); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ title: s.title, date: s.date, start_time: s.start_time, end_time: s.end_time, stage: s.stage, track: s.track || "", chair: s.chair || "", description: s.description || "", speaker_ids: s.speaker_ids || [], resources: s.resources || "", replay_url: s.replay_url || "" }); setSpeakerSearch(""); setCitations(parseCitations(s.resources)); setOpen(true); };

  const save = async () => {
    try {
      const resources = citations.map((c) => c.trim()).filter(Boolean).join("\n\n");
      const payload = { ...form, resources, replay_url: (form.replay_url || "").trim() };
      if (editing) await api.patch(`/sessions/${editing.id}`, payload);
      else await api.post("/sessions", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (id) => { if (!window.confirm("Delete session?")) return; await api.delete(`/sessions/${id}`); toast.success("Deleted"); load(); };

  const moveWithinBlock = async (sessionId, direction) => {
    try {
      const r = await api.post(`/sessions/${sessionId}/move?direction=${direction}`);
      if (r.data?.moved === false) return; // already at edge - no toast spam
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const importCSV = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/sessions/import", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.created} sessions`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleSpeaker = (id) => setForm((f) => ({ ...f, speaker_ids: f.speaker_ids.includes(id) ? f.speaker_ids.filter((x) => x !== id) : [...f.speaker_ids, id] }));

  // Sort: selected speakers first (so they're always visible without scrolling),
  // then everyone else alphabetical. Filter by the search box.
  const speakersOrdered = useMemo(() => {
    const q = speakerSearch.trim().toLowerCase();
    const selected = new Set(form.speaker_ids);
    const matched = speakers.filter((sp) => !q || (sp.name || "").toLowerCase().includes(q));
    return [...matched].sort((a, b) => {
      const aSel = selected.has(a.id) ? 0 : 1;
      const bSel = selected.has(b.id) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [speakers, speakerSearch, form.speaker_ids]);

  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <Button onClick={openNew} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="admin-new-session-btn"><Plus size={16} className="mr-1" /> New session</Button>
        <label className="inline-flex items-center h-10 px-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm cursor-pointer hover:border-[#000000] text-sm" data-testid="admin-import-csv">
          <UploadSimple size={16} className="mr-1" /> Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={importCSV} />
        </label>
        <BulkMoveButton onMoved={load} />
      </div>
      <div className="space-y-4">
        {groupSessionsByBlock(rows).map((block) => (
          <div key={block.key} className="space-y-2">
            {block.track && (
              <div className="flex items-baseline gap-2 px-1 pt-1" data-testid={`admin-block-header-${block.key}`}>
                <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">
                  {formatDateShortUK(block.date)} · {block.start_time} · {block.stage_label || block.stage}
                </span>
                <h3 className="font-display text-sm text-[#1c1c24] leading-tight">
                  {block.track}
                </h3>
              </div>
            )}
            {block.items.map((s, idx) => (
              <div key={s.id} className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-center gap-3" data-testid={`admin-session-row-${s.id}`}>
                <div className="font-mono-time text-xs text-[#000000] w-28 flex-shrink-0">{formatDateShortUK(s.date)} {s.start_time}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.title}</div>
                  <div className="text-xs text-[#7A828C] truncate">{s.stage_label || s.stage} · {(s.speakers || []).map((x) => x.name).join(", ")}</div>
                </div>
                {block.items.length > 1 && (
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => moveWithinBlock(s.id, "up")}
                      disabled={idx === 0}
                      className="w-7 h-7 grid place-items-center rounded-sm hover:bg-[#F0F2F7] disabled:opacity-30 disabled:hover:bg-transparent"
                      aria-label="Move earlier in block"
                      title="Move earlier in block"
                      data-testid={`move-session-up-${s.id}`}
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveWithinBlock(s.id, "down")}
                      disabled={idx === block.items.length - 1}
                      className="w-7 h-7 grid place-items-center rounded-sm hover:bg-[#F0F2F7] disabled:opacity-30 disabled:hover:bg-transparent"
                      aria-label="Move later in block"
                      title="Move later in block"
                      data-testid={`move-session-down-${s.id}`}
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                )}
                <MoveRoomButton session={s} onMoved={load} />
                <button onClick={() => openEdit(s)} className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7]" data-testid={`edit-session-${s.id}`}><PencilSimple size={16} /></button>
                <button onClick={() => del(s.id)} className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7] text-red-400" data-testid={`delete-session-${s.id}`}><Trash size={16} /></button>
              </div>
            ))}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E5E9F0] max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit session" : "New session"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="form-session-title" />
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm" data-testid="form-session-date" />
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="rounded-sm" data-testid="form-session-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-sm" data-testid="form-session-start" />
              <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="rounded-sm" data-testid="form-session-end" />
            </div>
            <Input placeholder="Track (optional)" value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })} className="rounded-sm" />
            <Input placeholder="Chair (optional) - applies to the whole session block" value={form.chair} onChange={(e) => setForm({ ...form, chair: e.target.value })} className="rounded-sm" data-testid="form-session-chair" />
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-sm" />
            <div>
              <Label className="text-xs font-mono-time uppercase tracking-widest text-[#7A828C] mb-1 block">
                Papers &amp; resources
              </Label>
              <p className="text-[11px] text-[#7A828C] mb-2">
                One paper per row. URLs, DOIs and PMIDs are auto-linked for delegates. Drag arrows to reorder - papers are released to delegates one-by-one from the session page.
              </p>
              <div className="space-y-2" data-testid="session-citations-list">
                {citations.map((cit, i) => (
                  <div key={i} className="flex gap-1.5 items-start" data-testid={`session-citation-row-${i}`}>
                    <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mt-2.5 w-5 flex-shrink-0">{i + 1}</div>
                    <Textarea
                      value={cit}
                      onChange={(e) => updateCitation(i, e.target.value)}
                      placeholder={i === 0 ? "e.g. Weick et al. The Sentence You Live By. NEJM. 2024. PMID: 41122846 https://doi.org/10.1056/NEJMxx12345" : "Next citation…"}
                      rows={3}
                      className="rounded-sm text-xs flex-1 font-mono-time"
                      data-testid={`session-citation-input-${i}`}
                    />
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => moveCitation(i, -1)}
                        disabled={i === 0}
                        className="w-7 h-7 grid place-items-center rounded-sm hover:bg-[#F0F2F7] disabled:opacity-30 disabled:hover:bg-transparent"
                        aria-label="Move up"
                        data-testid={`session-citation-up-${i}`}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveCitation(i, +1)}
                        disabled={i === citations.length - 1}
                        className="w-7 h-7 grid place-items-center rounded-sm hover:bg-[#F0F2F7] disabled:opacity-30 disabled:hover:bg-transparent"
                        aria-label="Move down"
                        data-testid={`session-citation-down-${i}`}
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeCitation(i)}
                        className="w-7 h-7 grid place-items-center rounded-sm hover:bg-[#F0F2F7] text-red-400"
                        aria-label="Remove citation"
                        data-testid={`session-citation-remove-${i}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                onClick={addCitation}
                variant="outline"
                className="rounded-sm mt-2 h-9"
                data-testid="session-citation-add-btn"
              >
                <Plus size={14} className="mr-1.5" /> Add paper
              </Button>
            </div>
            <div>
              <Label>Speakers</Label>
              <div className="relative mt-1">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7A828C] pointer-events-none" />
                <Input
                  type="text"
                  value={speakerSearch}
                  onChange={(e) => setSpeakerSearch(e.target.value)}
                  placeholder="Search speakers…"
                  className="rounded-sm pl-8"
                  data-testid="session-speaker-search"
                />
              </div>
              <div
                className="border border-[#E5E9F0] rounded-sm p-2 mt-1.5 overflow-y-auto"
                style={{ maxHeight: "min(50vh, 320px)", WebkitOverflowScrolling: "touch" }}
                data-testid="session-speaker-list"
              >
                {speakersOrdered.length === 0 && (
                  <p className="text-xs text-[#7A828C] py-2 text-center">No speakers match &quot;{speakerSearch}&quot;.</p>
                )}
                {speakersOrdered.map((sp) => {
                  const checked = form.speaker_ids.includes(sp.id);
                  return (
                    <label
                      key={sp.id}
                      className={`flex items-center gap-2 py-1.5 px-1 text-sm cursor-pointer rounded-sm ${
                        checked ? "bg-[#E6F8FC]" : "hover:bg-[#F8F8FB]"
                      }`}
                      data-testid={`session-speaker-row-${sp.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSpeaker(sp.id)}
                        className="w-4 h-4 flex-shrink-0"
                      />
                      <span className={checked ? "font-medium text-[#1c1c24]" : ""}>{sp.name}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#7A828C] mt-1">
                {form.speaker_ids.length} selected · selected speakers stay at the top of the list.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="form-session-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
